// /opt/isp_erp/frontend/src/components/tickets/CloseInstalacionDialog.jsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Inventory as InventoryIcon,
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
  ExpandMore as ExpandMoreIcon,
  Router as RouterIcon,
  SignalCellularAlt as SignalIcon,
  Vignette as VignetteIcon,
  Badge as BadgeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Engineering as EngineeringIcon
} from '@mui/icons-material';
import { ticketsAPI } from '../../api/tickets.api';
import FotoUploader from './FotoUploader';
import axios from '../../api/axios';
import { warehouseAPI } from '../../api/warehouse.api';

const CloseInstalacionDialog = ({ open, onClose, ticket, onSuccess }) => {
  const [materiales, setMateriales] = useState([]);
  const [nuevoMaterial, setNuevoMaterial] = useState({
    material_id: '',
    cantidad: 1,
    observaciones: ''
  });
  const [catalogoMateriales, setCatalogoMateriales] = useState([]);
  const [stockTecnico, setStockTecnico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cargandoMateriales, setCargandoMateriales] = useState(false);
  const [error, setError] = useState('');
  const [fotosBase64, setFotosBase64] = useState([]);

  // Estados para equipos
  const [equiposDisponibles, setEquiposDisponibles] = useState([]);
  const [equiposFiltrados, setEquiposFiltrados] = useState([]);
  const [equiposSeleccionados, setEquiposSeleccionados] = useState([]);
  const [searchEquipo, setSearchEquipo] = useState('');
  const [selectedEquipo, setSelectedEquipo] = useState(null);
  const [cargandoEquipos, setCargandoEquipos] = useState(false);
  const [expandedEquipos, setExpandedEquipos] = useState(true);

  // Estados para ubicación
  const [ubicacion, setUbicacion] = useState(null);
  const [obteniendoUbicacion, setObteniendoUbicacion] = useState(false);
  const [errorUbicacion, setErrorUbicacion] = useState('');
  const [usarUbicacion, setUsarUbicacion] = useState(true);

  // Estados para NAP
  const [naps, setNaps] = useState([]);
  const [selectedNapId, setSelectedNapId] = useState('');
  const [puertos, setPuertos] = useState([]);
  const [selectedPuerto, setSelectedPuerto] = useState('');
  const [nivelSenal, setNivelSenal] = useState('');
  const [cargandoNaps, setCargandoNaps] = useState(false);
  const [napsCercanos, setNapsCercanos] = useState([]);
  const [expandedNap, setExpandedNap] = useState(false);
  const [asignarNap, setAsignarNap] = useState(false);

  // Estados para viñeta y ficha
  const [vinetaSerial, setVinetaSerial] = useState('');
  const [fichaNumero, setFichaNumero] = useState('');
  const [vignettesDisponibles, setVignettesDisponibles] = useState([]);
  const [cargandoVignettes, setCargandoVignettes] = useState(false);
  const [asignarVineta, setAsignarVineta] = useState(false);

  // ==================== CARGAR DATOS ====================
  useEffect(() => {
    if (open && ticket?.assigned_technician_id) {
      cargarEquiposDelTecnico(ticket.assigned_technician_id);
      cargarMateriales();
      cargarStockTecnico(ticket.assigned_technician_id);
      cargarVignettesDisponibles(); // ✅ Carga viñetas BODEGA + ASIGNADAS al técnico
      cargarNaps();
      if (usarUbicacion) {
        obtenerUbicacion();
      }
    }
  }, [open, ticket]);

  // Filtrar equipos por búsqueda
  useEffect(() => {
    if (searchEquipo) {
      const filtrados = equiposDisponibles.filter(e =>
        e.sn?.toLowerCase().includes(searchEquipo.toLowerCase()) ||
        e.modelo?.toLowerCase().includes(searchEquipo.toLowerCase())
      );
      setEquiposFiltrados(filtrados);
    } else {
      setEquiposFiltrados(equiposDisponibles);
    }
  }, [searchEquipo, equiposDisponibles]);

  // ==================== FUNCIONES PARA EQUIPOS ====================
  const cargarEquiposDelTecnico = async (tecnicoId) => {
    setCargandoEquipos(true);
    try {
      const enTransitoRes = await warehouseAPI.listarEquipos({
        estado: 'EN_TRANSITO',
        poseedor_id: tecnicoId
      });

      let equipos = enTransitoRes.data || [];

      if (equipos.length === 0) {
        const bodegaRes = await warehouseAPI.listarEquipos({
          estado: 'BODEGA'
        });
        equipos = bodegaRes.data || [];
      }

      setEquiposDisponibles(equipos);
      setEquiposFiltrados(equipos);

      if (ticket?.materials_used?.equipos && ticket.materials_used.equipos.length > 0) {
        const equiposPreSeleccionados = ticket.materials_used.equipos.map(e => ({
          sn: e.sn,
          tipo: e.tipo || 'ONU',
          modelo: e.modelo || 'DESCONOCIDO'
        }));
        setEquiposSeleccionados(equiposPreSeleccionados);
      }
    } catch (error) {
      console.error('Error cargando equipos del técnico:', error);
      setEquiposDisponibles([]);
      setEquiposFiltrados([]);
    } finally {
      setCargandoEquipos(false);
    }
  };

  const agregarEquipo = (equipo) => {
    if (!equiposSeleccionados.find(e => e.sn === equipo.sn)) {
      setEquiposSeleccionados([...equiposSeleccionados, {
        sn: equipo.sn,
        tipo: equipo.tipoSeleccionado || 'ONU',
        modelo: equipo.modelo || 'DESCONOCIDO'
      }]);
    }
    setSelectedEquipo(null);
    setSearchEquipo('');
  };

  const eliminarEquipo = (index) => {
    const nuevosEquipos = [...equiposSeleccionados];
    nuevosEquipos.splice(index, 1);
    setEquiposSeleccionados(nuevosEquipos);
  };

  // ==================== ✅ FUNCIÓN CORREGIDA PARA VIÑETAS ====================


  const cargarVignettesDisponibles = async () => {
    setCargandoVignettes(true);
    try {
      // ✅ Usar el endpoint con filtro por técnico
      const response = await axios.get(`/tickets/${ticket.id}/vinetas-disponibles`);
      const data = response.data || {};
      const vignettes = data.vinetas_disponibles || [];

      // ✅ Filtrar adicionalmente en frontend (por si acaso)

      const vignettesConOrigen = vignettes.map(v => ({
         ...v,
         origen: 'ASIGNADO_A_TI'
      }));

      setVignettesDisponibles(vignettesConOrigen);

      console.log('✅ Viñetas del técnico:', {
        tecnico_id: data.tecnico_id,
        tecnico_nombre: data.tecnico_nombre,
        total: vignettesConOrigen.length,
        detalles: vignettesConOrigen.map(v => `${v.serial} (${v.estado})`)
      });


    } catch (error) {
      console.error('❌ Error cargando viñetas:', error);
      setVignettesDisponibles([]);
    } finally {
      setCargandoVignettes(false);
    }
  };



  // ==================== FUNCIONES PARA NAP ====================
  const cargarNaps = async () => {
    setCargandoNaps(true);
    try {
      const response = await axios.get('/ftth/naps');
      setNaps(response.data || []);
    } catch (error) {
      console.error('Error cargando NAPs:', error);
    } finally {
      setCargandoNaps(false);
    }
  };

  const buscarNapsCercanos = async (lat, lng) => {
    try {
      const response = await axios.get(`/ftth/naps/nearby?lat=${lat}&lng=${lng}&radius_km=2`);
      setNapsCercanos(response.data || []);
    } catch (error) {
      console.error('Error buscando NAPs cercanos:', error);
    }
  };

  const cargarPuertosNap = async (napId) => {
    if (!napId) return;
    try {
      const response = await axios.get(`/ftth/naps/${napId}/puertos?estado=LIBRE`);
      setPuertos(response.data || []);
      setSelectedPuerto('');
    } catch (error) {
      console.error('Error cargando puertos:', error);
      setPuertos([]);
    }
  };

  const handleNapChange = (napId) => {
    setSelectedNapId(napId);
    if (napId) {
      cargarPuertosNap(napId);
    } else {
      setPuertos([]);
      setSelectedPuerto('');
    }
  };

  // ==================== FUNCIONES PARA UBICACIÓN ====================
  const obtenerUbicacion = () => {
    if (!navigator.geolocation) {
      setErrorUbicacion('Geolocalización no soportada');
      setUsarUbicacion(false);
      return;
    }

    setObteniendoUbicacion(true);
    setErrorUbicacion('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const ubicacionData = {
          latitud: position.coords.latitude,
          longitud: position.coords.longitude,
          precision: position.coords.accuracy
        };
        setUbicacion(ubicacionData);
        buscarNapsCercanos(ubicacionData.latitud, ubicacionData.longitud);
        setObteniendoUbicacion(false);
      },
      (error) => {
        console.error('Error de geolocalización:', error);
        let mensaje = '';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            mensaje = 'Permiso denegado. Activa la ubicación en tu dispositivo.';
            break;
          case error.POSITION_UNAVAILABLE:
            mensaje = 'No se pudo obtener la ubicación. Verifica tu GPS.';
            break;
          case error.TIMEOUT:
            mensaje = 'Tiempo de espera agotado. Intenta nuevamente.';
            break;
          default:
            mensaje = 'Error al obtener ubicación.';
        }
        setErrorUbicacion(mensaje);
        setObteniendoUbicacion(false);
        setUsarUbicacion(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // ==================== FUNCIONES PARA MATERIALES ====================
  const cargarMateriales = async () => {
    try {
      const response = await axios.get('/warehouse/materiales');
      setCatalogoMateriales(response.data || []);
    } catch (error) {
      console.error('Error cargando materiales:', error);
    }
  };

  const cargarStockTecnico = async (tecnicoId) => {
    setCargandoMateriales(true);
    try {
      const response = await axios.get(`/warehouse/materiales/stock-tecnico/${tecnicoId}`);
      setStockTecnico(response.data || []);
    } finally {
      setCargandoMateriales(false);
    }
  };

  const agregarMaterial = () => {
    if (!nuevoMaterial.material_id) {
      setError('Seleccione un material');
      return;
    }
    if (!nuevoMaterial.cantidad || nuevoMaterial.cantidad < 1) {
      setError('Ingrese una cantidad válida');
      return;
    }

    const materialStock = stockTecnico.find(s => s.material_id === nuevoMaterial.material_id);
    if (!materialStock || materialStock.cantidad < nuevoMaterial.cantidad) {
      const disponible = materialStock?.cantidad || 0;
      setError(`Stock insuficiente. Disponible: ${disponible}`);
      return;
    }

    const materialInfo = catalogoMateriales.find(m => m.id === nuevoMaterial.material_id);

    setMateriales([...materiales, {
      material_id: nuevoMaterial.material_id,
      nombre: materialInfo?.nombre,
      unidad: materialInfo?.unidad,
      cantidad: nuevoMaterial.cantidad,
      observaciones: nuevoMaterial.observaciones
    }]);

    setNuevoMaterial({ material_id: '', cantidad: 1, observaciones: '' });
    setError('');
  };

  const eliminarMaterial = (index) => {
    setMateriales(materiales.filter((_, i) => i !== index));
  };

  // ==================== HANDLE SUBMIT ====================
  const handleSubmit = async () => {
    if (equiposSeleccionados.length === 0) {
      setError('Debe seleccionar al menos un equipo a instalar');
      return;
    }

    if (materiales.length === 0) {
      setError('Debe registrar al menos un material utilizado');
      return;
    }

    if (asignarNap && !selectedNapId) {
      setError('Debe seleccionar una caja NAP');
      return;
    }
    if (asignarNap && selectedNapId && !selectedPuerto) {
      setError('Debe seleccionar un puerto del NAP');
      return;
    }

    if (asignarVineta && !vinetaSerial) {
      setError('Debe seleccionar una viñeta');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const materialesEnvio = materiales.map(m => ({
        material_id: m.material_id,
        cantidad: m.cantidad,
        observaciones: m.observaciones || ''
      }));

      const equiposEnvio = equiposSeleccionados.map(e => ({
        sn: e.sn,
        tipo: e.tipo || 'ONU',
        modelo: e.modelo || 'DESCONOCIDO'
      }));

      let ubicacionData = null;
      if (usarUbicacion && ubicacion) {
        ubicacionData = {
          latitud: ubicacion.latitud,
          longitud: ubicacion.longitud,
          precision: ubicacion.precision
        };
      }

      let napData = null;
      if (asignarNap && selectedNapId && selectedPuerto) {
        napData = {
          nap_id: parseInt(selectedNapId),
          puerto_nap: parseInt(selectedPuerto),
          nivel_senal_rx: nivelSenal ? parseFloat(nivelSenal) : null
        };
      }

      let vinetaData = null;
      if (asignarVineta && vinetaSerial) {
        vinetaData = {
          vineta_serial: vinetaSerial
        };
      }

      const fichaEnvio = fichaNumero ? String(fichaNumero) : null;

      await ticketsAPI.closeInstalacionTicket(
        ticket.id,
        materialesEnvio,
        equiposEnvio,
        ubicacionData,
        napData,
        fotosBase64,
        vinetaData,
        fichaEnvio
      );

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error completo:', err);
      console.error('Response:', err.response);

      let errorMessage = 'Error al cerrar el ticket';
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else if (typeof err.response.data.detail === 'object') {
          errorMessage = err.response.data.detail.msg || JSON.stringify(err.response.data.detail);
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!ticket) return null;

  const tecnicoId = ticket.assigned_technician_id;
  const tecnicoNombre = ticket.assigned_technician_name || 'No asignado';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Completar Instalación
        <Typography variant="body2" color="text.secondary">
          Ticket: {ticket.ticket_number} - {ticket.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Técnico: {tecnicoNombre} {tecnicoId ? `(ID: ${tecnicoId})` : ''}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>📋 Instrucciones:</strong> Seleccione los equipos a instalar, registre los materiales utilizados,
            y complete los datos de NAP, viñeta y ubicación.
          </Alert>
        </Box>

        {/* ==================== SECCIÓN: EQUIPOS A INSTALAR ==================== */}
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#e3f2fd' }}>
          <Accordion expanded={expandedEquipos} onChange={() => setExpandedEquipos(!expandedEquipos)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EngineeringIcon color="primary" />
                Equipos a Instalar ({equiposSeleccionados.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {cargandoEquipos ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 2 }}>Cargando equipos disponibles...</Typography>
                </Box>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                    <strong>📦 Equipos disponibles:</strong> {equiposDisponibles.length} equipos en tránsito o bodega.
                    Seleccione los que va a instalar.
                  </Alert>

                  <TextField
                    fullWidth
                    placeholder="Buscar equipo por SN o modelo..."
                    value={searchEquipo}
                    onChange={(e) => setSearchEquipo(e.target.value)}
                    size="small"
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
                      endAdornment: searchEquipo && (
                        <IconButton size="small" onClick={() => setSearchEquipo('')}>
                          <ClearIcon />
                        </IconButton>
                      )
                    }}
                  />

                  <List dense sx={{ maxHeight: 200, overflow: 'auto', mb: 2, bgcolor: '#fafafa', borderRadius: 1 }}>
                    {equiposFiltrados.length === 0 ? (
                      <ListItem>
                        <ListItemText
                          primary="No hay equipos disponibles"
                          secondary={searchEquipo ? 'Prueba con otra búsqueda' : 'No hay equipos en tránsito o bodega'}
                        />
                      </ListItem>
                    ) : (
                      equiposFiltrados.map(equipo => {
                        const yaSeleccionado = equiposSeleccionados.some(e => e.sn === equipo.sn);
                        return (
                          <ListItem
                            key={equipo.id}
                            button
                            onClick={() => !yaSeleccionado && setSelectedEquipo(equipo)}
                            selected={selectedEquipo?.id === equipo.id}
                            sx={{
                              borderRadius: 1,
                              mb: 0.5,
                              bgcolor: yaSeleccionado ? '#e8f5e9' : 'transparent'
                            }}
                            disabled={yaSeleccionado}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" fontWeight="bold">{equipo.sn}</Typography>
                                  {yaSeleccionado && <Chip label="✓ Seleccionado" color="success" size="small" />}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="caption" display="block">Modelo: {equipo.modelo}</Typography>
                                  <Typography variant="caption" display="block">
                                    Estado: {equipo.estado_actual} | Poseedor: {equipo.poseedor_nombre || 'N/A'}
                                  </Typography>
                                </Box>
                              }
                            />
                            {!yaSeleccionado && (
                              <Chip label="Agregar" color="primary" size="small" clickable />
                            )}
                          </ListItem>
                        );
                      })
                    )}
                  </List>

                  {selectedEquipo && !equiposSeleccionados.some(e => e.sn === selectedEquipo.sn) && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                      <FormControl size="small" sx={{ flex: 1 }}>
                        <InputLabel>Tipo</InputLabel>
                        <Select
                          value={selectedEquipo?.tipoSeleccionado || 'ONU'}
                          onChange={(e) => {
                            if (selectedEquipo) {
                              setSelectedEquipo({...selectedEquipo, tipoSeleccionado: e.target.value});
                            }
                          }}
                          label="Tipo"
                        >
                          <MenuItem value="ONU">📡 ONU</MenuItem>
                          <MenuItem value="ROUTER">📶 Router</MenuItem>
                          <MenuItem value="BRIDGE">🔗 Bridge</MenuItem>
                          <MenuItem value="SWITCH">🔄 Switch</MenuItem>
                        </Select>
                      </FormControl>
                      <Button variant="contained" startIcon={<AddIcon />} onClick={() => agregarEquipo(selectedEquipo)}>
                        Agregar
                      </Button>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2">Equipos seleccionados para instalar:</Typography>
                  {equiposSeleccionados.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">Ningún equipo seleccionado</Typography>
                  ) : (
                    equiposSeleccionados.map((eq, idx) => (
                      <Paper key={idx} sx={{ p: 1, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#e8f5e9' }}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">{eq.sn}</Typography>
                          <Typography variant="caption">{eq.modelo} | Tipo: {eq.tipo}</Typography>
                        </Box>
                        <IconButton size="small" color="error" onClick={() => eliminarEquipo(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    ))
                  )}
                </>
              )}
            </AccordionDetails>
          </Accordion>
        </Paper>

        {/* ==================== SECCIÓN: VIÑETA Y FICHA ==================== */}
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#fff3e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VignetteIcon color="warning" />
              Viñeta y Ficha
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={asignarVineta}
                  onChange={(e) => setAsignarVineta(e.target.checked)}
                  size="small"
                  color="warning"
                />
              }
              label="Asignar Viñeta"
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {asignarVineta ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Viñeta (Serial)</InputLabel>
                  <Select
                    value={vinetaSerial}
                    onChange={(e) => setVinetaSerial(e.target.value)}
                    label="Viñeta (Serial)"
                    disabled={cargandoVignettes}
                  >
                    <MenuItem value="">Seleccionar viñeta...</MenuItem>
                    {vignettesDisponibles.map((v) => (
                      <MenuItem key={v.id} value={v.serial}>
                        {v.serial} - {v.nombre}
                        {v.origen === 'ASIGNADO_A_TI' ? ' ✅ (Tuya)' : ' 📦 (Bodega)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="Viñeta (Serial)"
                  placeholder="Ej: VIN-2024-001"
                  value={vinetaSerial}
                  onChange={(e) => setVinetaSerial(e.target.value)}
                  disabled={!asignarVineta}
                  helperText={cargandoVignettes ? 'Cargando viñetas...' : 'Ingrese el serial de la viñeta o seleccione del listado'}
                />
              )}
              {cargandoVignettes && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">Cargando viñetas disponibles...</Typography>
                </Box>
              )}
              {vignettesDisponibles.length === 0 && !cargandoVignettes && asignarVineta && (
                <Alert severity="warning" sx={{ mt: 1, fontSize: '0.75rem' }}>
                  No hay viñetas disponibles en bodega ni asignadas a ti.
                </Alert>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Número de Ficha"
                placeholder="Ej: F-2024-001"
                value={fichaNumero || ''}
                onChange={(e) => setFichaNumero(e.target.value)}
                InputProps={{
                  startAdornment: <BadgeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                helperText="Ingrese el número de ficha que registró el técnico"
              />
            </Grid>
          </Grid>

          {asignarVineta && vinetaSerial && (
            <Alert severity="info" sx={{ mt: 2, fontSize: '0.8rem' }}>
              <strong>✅ Viñeta seleccionada:</strong> {vinetaSerial} - Será asignada al cliente al cerrar el ticket.
            </Alert>
          )}
          {fichaNumero && (
            <Alert severity="info" sx={{ mt: 1, fontSize: '0.8rem' }}>
              <strong>📋 Ficha:</strong> {fichaNumero} - Será registrada en el cliente.
            </Alert>
          )}
        </Paper>

        {/* ==================== SECCIÓN: NAP ==================== */}
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#e8f5e9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RouterIcon color="primary" />
              Asignación a Caja NAP
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={asignarNap}
                  onChange={(e) => setAsignarNap(e.target.checked)}
                  size="small"
                  color="primary"
                />
              }
              label="Asignar NAP"
            />
          </Box>

          {asignarNap && (
            <>
              {napsCercanos.length > 0 && (
                <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                  <strong>📡 NAPs cercanos (≤2km):</strong>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {napsCercanos.map(nap => (
                      <Button
                        key={nap.id}
                        size="small"
                        variant={selectedNapId === nap.id ? 'contained' : 'outlined'}
                        onClick={() => handleNapChange(nap.id)}
                        sx={{ fontSize: '0.7rem' }}
                      >
                        {nap.codigo} ({nap.distancia_km}km) - {nap.puertos_libres} libres
                      </Button>
                    ))}
                  </Box>
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Caja NAP</InputLabel>
                    <Select
                      value={selectedNapId}
                      onChange={(e) => handleNapChange(e.target.value)}
                      label="Caja NAP"
                      disabled={cargandoNaps}
                    >
                      <MenuItem value="">Seleccionar NAP...</MenuItem>
                      {naps.map((nap) => (
                        <MenuItem key={nap.id} value={nap.id}>
                          {nap.codigo} - {nap.nombre} ({nap.puertos_libres} libres / {nap.porcentaje_ocupacion}% ocupado)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Puerto NAP</InputLabel>
                    <Select
                      value={selectedPuerto}
                      onChange={(e) => setSelectedPuerto(e.target.value)}
                      label="Puerto NAP"
                      disabled={!selectedNapId || puertos.length === 0}
                    >
                      <MenuItem value="">Seleccionar puerto...</MenuItem>
                      {puertos.map((puerto) => (
                        <MenuItem key={puerto.id} value={puerto.numero_puerto}>
                          Puerto {puerto.numero_puerto} - {puerto.estado}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nivel de Señal RX (dBm)"
                    placeholder="Ej: -18.5"
                    value={nivelSenal}
                    onChange={(e) => setNivelSenal(e.target.value)}
                    InputProps={{
                      startAdornment: <SignalIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    helperText="Rango óptimo: -20 a -25 dBm"
                  />
                </Grid>
              </Grid>

              {selectedNapId && (
                <Accordion
                  expanded={expandedNap}
                  onChange={() => setExpandedNap(!expandedNap)}
                  sx={{ mt: 2, bgcolor: '#f5f5f5' }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2">📦 Información del NAP seleccionado</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {naps.find(n => n.id === parseInt(selectedNapId)) && (
                      <Box>
                        <Typography variant="body2">
                          <strong>Código:</strong> {naps.find(n => n.id === parseInt(selectedNapId)).codigo}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Dirección:</strong> {naps.find(n => n.id === parseInt(selectedNapId)).direccion}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Ocupación:</strong> {naps.find(n => n.id === parseInt(selectedNapId)).puertos_ocupados}/
                          {naps.find(n => n.id === parseInt(selectedNapId)).capacidad_puertos} puertos
                          ({naps.find(n => n.id === parseInt(selectedNapId)).porcentaje_ocupacion}%)
                        </Typography>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}
            </>
          )}
        </Paper>

        {/* ==================== SECCIÓN: FOTOS ==================== */}
        <FotoUploader onFotosChange={setFotosBase64} maxFotos={9} />

        {/* ==================== SECCIÓN: UBICACIÓN ==================== */}
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon color="primary" />
              Ubicación de Instalación
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={usarUbicacion}
                  onChange={(e) => setUsarUbicacion(e.target.checked)}
                  size="small"
                />
              }
              label="Guardar ubicación"
            />
          </Box>

          {usarUbicacion && (
            <>
              {!ubicacion && !obteniendoUbicacion && !errorUbicacion && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<MyLocationIcon />}
                  onClick={obtenerUbicacion}
                  fullWidth
                >
                  Obtener ubicación actual
                </Button>
              )}

              {obteniendoUbicacion && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Obteniendo ubicación...</Typography>
                </Box>
              )}

              {errorUbicacion && (
                <Alert severity="warning" size="small" sx={{ mt: 1 }}>
                  {errorUbicacion}
                </Alert>
              )}

              {ubicacion && (
                <Alert severity="success" sx={{ mt: 1 }} icon={<MyLocationIcon />}>
                  <Typography variant="caption" display="block">
                    Lat: {ubicacion.latitud.toFixed(6)} | Lng: {ubicacion.longitud.toFixed(6)}
                  </Typography>
                  {ubicacion.precision && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Precisión: ±{Math.round(ubicacion.precision)}m
                    </Typography>
                  )}
                </Alert>
              )}
            </>
          )}
        </Paper>

        {/* ==================== SECCIÓN: MATERIALES ==================== */}
        <Typography variant="h6" gutterBottom>
          <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Materiales Utilizados
        </Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <FormControl fullWidth size="small">
                <InputLabel>Material</InputLabel>
                <Select
                  value={nuevoMaterial.material_id}
                  onChange={(e) => setNuevoMaterial({ ...nuevoMaterial, material_id: e.target.value })}
                  label="Material"
                >
                  {catalogoMateriales.map((m) => {
                    const stock = stockTecnico.find(s => s.material_id === m.id);
                    const stockTexto = stock ? `(${stock.cantidad} ${m.unidad})` : '(sin stock)';
                    return (
                      <MenuItem key={m.id} value={m.id}>
                        {m.nombre} {stockTexto}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Cantidad"
                size="small"
                value={nuevoMaterial.cantidad}
                onChange={(e) => setNuevoMaterial({ ...nuevoMaterial, cantidad: parseFloat(e.target.value) || 1 })}
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Observaciones"
                size="small"
                value={nuevoMaterial.observaciones}
                onChange={(e) => setNuevoMaterial({ ...nuevoMaterial, observaciones: e.target.value })}
                placeholder="Ej: Conector SC/APC"
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="contained"
                onClick={agregarMaterial}
                startIcon={<AddIcon />}
                disabled={!nuevoMaterial.material_id}
              >
                Agregar
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {cargandoMateriales && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="caption">Cargando stock del técnico...</Typography>
          </Box>
        )}

        {materiales.length > 0 && (
          <List sx={{ mb: 2 }}>
            <Divider />
            {materiales.map((mat, idx) => (
              <React.Fragment key={idx}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box>
                        <strong>{mat.nombre}</strong> x{mat.cantidad} {mat.unidad}
                        {mat.observaciones && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {mat.observaciones}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => eliminarMaterial(idx)} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          disabled={loading || equiposSeleccionados.length === 0 || materiales.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
        >
          {loading ? 'Cerrando...' : 'Completar Instalación'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloseInstalacionDialog;
